import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthModal from "../components/AuthModal";
import CosmicBackground from "../components/CosmicBackground";
import SubscribeSheet from "../components/SubscribeSheet";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { savePendingGuestQuestion } from "../hooks/useGuestChat";
import { useCareerMotion } from "./useCareerMotion";
import "./LovePage.css";

/**
 * /love-astrology: love, marriage timing and kundli compatibility.
 *
 * NOT /love. That route already exists and is a geo-gated page for FOREIGN
 * visitors; InternationalRoute sends Indian traffic home. This page is written
 * for exactly the visitor that gate turns away, so it needed its own URL.
 *
 * Slug chosen from Search Console rather than taste. Over 90 days the love
 * cluster ("love astrology free chat", "ai love astrology", "love life
 * astrology ai", "ai love prediction") takes ~90 clicks with no page behind it,
 * at positions 3.3-4.0. "Compatibility" was the original suggestion and earns
 * literally nothing, besides being the word /kundli-matching already owns.
 *
 * OVERLAP, known and deliberate: this page also covers marriage timing, which
 * is a BIGGER cluster (~180 clicks: "ai marriage prediction", "marriage
 * prediction by date of birth free ai"), and carries a gun milan section that
 * competes with /kundli-matching. See the note in the README of this change.
 *
 * RETARGET 2026-10-13 (item 22): GSC 28d, the head "love astrology free
 * chat" (8 clicks / 220 imp @7.8, 3.6% CTR, 220 of the cluster's 293
 * impressions) is HOMEPAGE 1-of-1; this page appears for none of its own
 * cluster and its whole query list is marriage spillover ("ai marriage
 * prediction" variants), which item 20's /marriage retarget will absorb.
 * The AI variants convert when seen ("love astrology ai" 33.3% @3.3, "ai
 * love astrology" 30.8% @2.1). Title/og/description/sub now lead with the
 * chat head; marriage stays in the body but leaves the title so /marriage
 * can own it.
 *
 * Same port-then-adapt treatment as /career: design markup and stylesheet
 * carried over verbatim, every selector scoped under .lov-root, brand palette
 * and fonts applied on top, no em or en dashes.
 *
 * Not ported: the reviewer strip and A/B H1 switcher (dev harness), the page's
 * own header and footer, pricing (the design carries placeholder prices), the
 * three review slots (PLACEHOLDER text), and the ask <dialog>, whose
 * question -> details -> OTP flow is the guest-chat flow we already ship.
 */

/* Chip key -> question. The design's own data-q values, which also back the
   ?q= deep link so a campaign can land on a pre-chosen question. */
const QUESTIONS: Record<string, string> = {
  "shaadi-kab": "Meri shaadi kab hogi?",
  "going-anywhere": "Is this relationship going anywhere?",
  "love-arranged": "Love marriage ya arranged?",
  "kundli-nahi-mili": "Kundli nahi mili, ab kya?",
  rishta: "Should I say yes to this rishta?",
  pattern: "Why do my relationships keep ending the same way?",
};

const FAQ_SCHEMA: Array<[string, string]> = [
  [
    "Kya ye sach mein accurate hai?",
    "Your kundli is computed from Swiss Ephemeris astronomical data, and Aura shows you which placement and period each insight comes from. It points to favourable windows, not fixed dates. That's how Vedic timing actually works.",
  ],
  [
    "Will it tell me if my partner is cheating, or if my ex will come back?",
    "No. Aura reads your chart, not someone else's private life. It can show your relationship patterns and the periods that favour big decisions.",
  ],
  [
    "Birth time nahi pata?",
    "It works. An approximate time still builds the chart and most of it can be read. An exact time sharpens the lagna and the dasha timing, so give the closest time you can.",
  ],
  [
    "What happens after my free question?",
    "You get a complete answer. If you want timing windows, patterns and the full picture, the Love & Marriage Report goes deeper.",
  ],
  [
    "App download karna padega?",
    "No. It works right here in your browser. There's also an Android app.",
  ],
  [
    "Is my data private?",
    "Yes. It is encrypted and never sold.",
  ],
  [
    "What is love astrology?",
    "Love astrology reads the part of your birth chart that describes relationships: the 7th house and its lord for partnership, Venus for attraction and affection, the 5th for romance, and the dasha period you are running for timing. Read together they describe the kind of partnership your chart leans toward and which phase you are in, rather than naming a person.",
  ],
  [
    "Can astrology predict marriage timing from a date of birth?",
    "Marriage timing needs the date, the time and the place, not the date alone. Those three fix the lagna, the house cusps and the Vimshottari dasha sequence, and it is the dasha and the transits of Jupiter and Saturn that mark the favourable windows. A chart shows windows, not fixed dates, and anyone offering you an exact date is guessing.",
  ],
  [
    "What does a gun milan score actually mean?",
    "Ashtakoot gun milan scores eight factors for a total of 36 gunas. Traditionally 18 or more is considered workable, but the number on its own says little: Nadi and Bhakoot carry the most weight, and a high score with a Nadi dosha is a different situation from a middling score without one. The score is a starting point for a conversation, not a verdict on a relationship.",
  ],
  [
    "Is the love astrology chat free?",
    "Your first question is free, with no card. Aura AI builds your kundli from your date, time and place of birth, then chats with you about your relationship in Hindi or English. After the free question, further chats are paid from wallet credits, with no subscription.",
  ],
];

export default function LovePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  useCareerMotion(rootRef);
  const [authOpen, setAuthOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [pendingReport, setPendingReport] = useState(false);
  const [pendingLoveReport, setPendingLoveReport] = useState(false);
  const [pendingSubscribe, setPendingSubscribe] = useState(false);

  const {
    user,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    pricingVariant,
  } = useAuth();

  const { prices, formatPrice } = usePricing();
  const reportPrice = prices["report_premium"] ?? 0;

  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp],
  );
  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );
  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => verifyMsg91Otp({ ...payload, variant: pricingVariant }),
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  /* Guests get the login sheet HERE rather than a trip to /chat: ChatRoute
     bounces a logged-out Indian visitor to the homepage, and this page is
     written for that exact visitor. Same reasoning as /career. */
  const ask = (question?: string) => {
    if (question) savePendingGuestQuestion(question);
    if (user) {
      navigate("/chat");
      return;
    }
    setAuthOpen(true);
  };

  /* The compatibility CTAs open the real Compatibility Report flow, the same
     one KundliMatch hands off to. */
  const openCompatibility = () => {
    if (user) {
      navigate("/compatibility-report");
      return;
    }
    setPendingReport(true);
    setAuthOpen(true);
  };

  /* The Love Life Report lives in the AI tab on /reports, the same place
     ReportUpsellCard sends people. /reports is behind ProtectedRoute, so a
     guest signs in first and the effect below carries them the rest of the
     way. Distinct from openCompatibility, which is the two-chart gun milan
     flow, not a single-chart report. */
  const openLoveReport = () => {
    if (user) {
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
      return;
    }
    setPendingLoveReport(true);
    setAuthOpen(true);
  };

  const openMembership = () => {
    if (user) {
      setSubscribeOpen(true);
      return;
    }
    setPendingSubscribe(true);
    setAuthOpen(true);
  };

  useEffect(() => {
    if (user && pendingReport) {
      setPendingReport(false);
      navigate("/compatibility-report");
    }
  }, [user, pendingReport, navigate]);

  useEffect(() => {
    if (user && pendingLoveReport) {
      setPendingLoveReport(false);
      navigate("/reports", { state: { activeTab: "ai", fromProceed: true } });
    }
  }, [user, pendingLoveReport, navigate]);

  useEffect(() => {
    if (user && pendingSubscribe) {
      setPendingSubscribe(false);
      setSubscribeOpen(true);
    }
  }, [user, pendingSubscribe]);

  /* ?q= preselects a chip, as in the design. */
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q");
    if (q && QUESTIONS[q]) {
      rootRef.current
        ?.querySelectorAll<HTMLElement>(".chip")
        .forEach((c) => c.setAttribute("aria-pressed", String(c.dataset.q === q)));
    }
  }, [location.search]);

  /* One delegated handler, so the ported markup needs no per-button props. */
  const onClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    const chip = el.closest<HTMLElement>(".chip");
    if (chip?.dataset.q) {
      ask(QUESTIONS[chip.dataset.q]);
      return;
    }
    if (el.closest("[data-compat]")) {
      openCompatibility();
      return;
    }
    if (el.closest("[data-report]")) {
      openLoveReport();
      return;
    }
    if (el.closest("[data-subscribe]")) {
      openMembership();
      return;
    }
    const cta = el.closest<HTMLElement>("[data-ask]");
    if (cta) {
      ask(cta.dataset.preset ? QUESTIONS[cta.dataset.preset] : undefined);
    }
  };

  return (
    <div className="lov-root" ref={rootRef} onClick={onClick}>
      <CosmicBackground />

      <Helmet>
        <title>Love Astrology Free Chat: AI Love Prediction by Date of Birth</title>
        <meta
          name="description"
          content="Free love astrology chat with Aura AI. Ask about your relationship, marriage timing and kundli matching, read from your 7th house, Venus and dashas."
        />
        <link rel="canonical" href="https://astroaura.ai/love-astrology/" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@400;500&display=swap"
        />
        <meta property="og:title" content="Love Astrology Free Chat: AI Love Prediction" />
        <meta
          property="og:description"
          content="Is this the one? Ask Aura AI using your exact birth chart. First question free."
        />
        <meta property="og:url" content="https://astroaura.ai/love-astrology/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_SCHEMA.map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Aura AI", item: "https://astroaura.ai/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Love Astrology Free Chat",
                item: "https://astroaura.ai/love-astrology/",
              },
            ],
          })}
        </script>
      </Helmet>

      <main>
        <section className="hero" aria-labelledby="h1">
          <div className="wrap">
            <div>
              <div className="trust"><span><b>★ 4.4</b> on Google Play</span><span><b>1M+</b> downloads</span><span>Private &amp; encrypted</span></div>
              <h1 id="h1" data-variant="a">Is this the one? Your chart has a clearer answer than <em>your group chat.</em></h1>
              <p className="sub"><strong>A free love astrology chat, read from your exact birth chart.</strong> Aura AI reads your <strong>7th house, Venus, dashas and 100+ placements</strong> together, then explains your love and marriage timeline in plain Hindi or English.</p>
              <button className="btn" type="button" data-ask>Ask My Love Question, Free <span aria-hidden="true">→</span></button>
              <div className="micro"><span>First question free</span><span>No card needed</span><span>Takes 60 seconds</span></div>

              <p className="chips-label" id="chips-label">Or tap a question</p>
              <div className="chips" role="group" aria-labelledby="chips-label">
                <button className="chip" type="button" data-q="shaadi-kab">Meri shaadi kab hogi?</button>
                <button className="chip" type="button" data-q="going-anywhere">Ye rishta chalega?</button>
                <button className="chip" type="button" data-q="love-arranged">Love ya arranged?</button>
                <button className="chip" type="button" data-q="kundli-nahi-mili">Kundli nahi mili</button>
                <button className="chip" type="button" data-q="rishta">Haan bolun ya nahi?</button>
                <button className="chip" type="button" data-q="pattern">Rishte tootte kyun?</button>
              </div>
            </div>

            <aside className="reading" aria-label="Sample reading">
              <div className="reading-head"><span className="tag">Sample reading</span><span className="tag">Hinglish</span></div>
              <div className="kundli">
                <svg viewBox="0 0 300 300" role="img" aria-label="Sample North Indian kundli with the 7th house highlighted">
                  <rect x="1" y="1" width="298" height="298" fill="#140718" stroke="#44264b" strokeWidth="2"/>
                  <polygon points="150,150 225,225 150,300 75,225" fill="rgba(255,204,253,.18)" stroke="#ffccfd" strokeWidth="2"/>
                  <g stroke="#44264b" strokeWidth="2" fill="none">
                    <line x1="0" y1="0" x2="300" y2="300"/><line x1="300" y1="0" x2="0" y2="300"/>
                    <polygon points="150,0 300,150 150,300 0,150"/>
                  </g>
                  <polygon points="150,150 225,225 150,300 75,225" fill="none" stroke="#ffccfd" strokeWidth="2.5"/>
                  <g fontFamily="IBM Plex Mono, monospace" fontSize="15" fill="#8d7593" textAnchor="middle">
                    <text x="150" y="60">1</text><text x="75" y="22">2</text><text x="22" y="80">3</text><text x="75" y="140">4</text>
                    <text x="22" y="230">5</text><text x="75" y="292">6</text><text x="150" y="212" fill="#ffccfd">7</text><text x="225" y="292">8</text>
                    <text x="278" y="230">9</text><text x="225" y="140">10</text><text x="278" y="80">11</text><text x="225" y="22">12</text>
                  </g>
                  <g fontFamily="Sora, sans-serif" fontSize="17" fontWeight="700" textAnchor="middle">
                    <text x="150" y="245" fill="#ffccfd">Ju</text>
                    <text x="38" y="258" fill="#f2cf8a">Ve</text>
                    <text x="75" y="170" fill="#f7eef8">Mo</text>
                    <text x="225" y="170" fill="#f7eef8">Sa</text>
                    <text x="150" y="90" fill="#bfa9c4">Su</text>
                  </g>
                </svg>
                <dl>
                  <div><dt>7th lord</dt><dd><span className="hl">Jupiter</span>, well placed</dd></div>
                  <div><dt>Next sub-period</dt><dd>Venus antardasha</dd></div>
                  <div><dt>Read together</dt><dd>100+ placements</dd></div>
                </dl>
              </div>
              <div className="msg user">Meri shaadi kab tak hogi? Ghar pe bahut pressure hai.</div>
              <div className="msg aura"><span className="who">Aura AI</span>Your 7th lord Jupiter is well placed, and your <strong>Venus antardasha</strong> starts next year. Traditionally that's a favourable phase for commitment, so the <strong>next 12-18 months</strong> look stronger than the next few. For now, your chart suggests getting clear on what you want rather than rushing a yes.</div>
              <p className="reading-foot">Example for illustration · Readings show windows, not fixed dates</p>
            </aside>
          </div>
        </section>


        <section className="rule" aria-labelledby="feel">
          <div className="wrap">
            <p className="eyebrow">Sound familiar?</p>
            <h2 id="feel" style={{marginTop: '14px'}}>You've asked everyone. <em>Everyone said something different.</em></h2>
            <div className="pains">
              <div className="pain"><q>"Shaadi kab kar rahe ho?"</q>At every family function.</div>
              <div className="pain"><q>Every friend is engaged.</q>You're still figuring out if this one's real.</div>
              <div className="pain"><q>A rishta is on the table.</q>And you have a week to say yes.</div>
              <div className="pain"><q>Kundli didn't match.</q>Nobody explained what that actually means.</div>
              <div className="pain last"><q>You can't ask your parents.</q>You can't ask your friends. You can ask this.</div>
            </div>
            <div className="center-cta"><button className="btn ghost" type="button" data-ask>Ask What You Can't Ask Anyone Else →</button></div>
          </div>
        </section>


        <section className="rule" aria-labelledby="why">
          <div className="wrap">
            <p className="eyebrow">Why this read is different</p>
            <h2 id="why" style={{marginTop: '14px'}}>Three astrologers. Three answers. <em>This one doesn't change.</em></h2>
            <div className="why">
              <article><h3>Same answer every time</h3><p>Ask today, ask next month: same chart, same logic. You won't get a different story from every person you ask.</p></article>
              <article><h3>100+ placements, not one house</h3><p>Most readings stop at the 7th house. Aura reads your 7th house, Venus, Jupiter, dashas, transits and 1,000+ yogas together.</p></article>
              <article><h3>It explains, it doesn't scare</h3><p>You see which placement and which period, so you decide for yourself. No gemstones or puja packages pushed at you.</p></article>
            </div>
            <div className="stats">
              <div><b>10M+</b><span>real charts read</span></div>
              <div><b>10K+</b><span>Vedic rules</span></div>
              <div><b>24x7</b><span>instant answers</span></div>
              <div><b>Swiss</b><span>Ephemeris astronomy data</span></div>
            </div>
          </div>
        </section>


        <section className="rule" aria-labelledby="milan">
          <div className="wrap milan">
            <div>
              <p className="eyebrow">Kundli matching</p>
              <h2 id="milan" style={{marginTop: '14px'}}>18 gun mile. <em>Par matlab kisi ne samjhaya?</em></h2>
              <p className="lede">Aura AI runs full Ashtakoot gun milan, Mangal dosha and Nadi dosha. Then it tells you what the score means for the two of you, in plain language. You get more than a number out of 36.</p>
              <div style={{marginTop: '26px'}}><button className="btn" type="button" data-compat>Check Our Compatibility →</button></div>
            </div>
            <div className="score" aria-label="Sample gun milan result">
              <div className="reading-head"><span className="tag">Sample match</span><span className="tag">Ashtakoot</span></div>
              <div className="score-top"><b>18</b><span>/ 36 gun</span></div>
              <div className="bar"><i></i></div>
              <div className="koot">
                <div><b>1/1</b>Varna</div><div className="low"><b>0.5/2</b>Vashya</div><div><b>1.5/3</b>Tara</div><div className="low"><b>1/4</b>Yoni</div>
                <div className="low"><b>0/5</b>Graha Maitri</div><div><b>6/6</b>Gana</div><div className="low"><b>0/7</b>Bhakoot</div><div><b>8/8</b>Nadi</div>
              </div>
              <div className="meaning"><span className="who">What it means</span>Gana and Nadi match fully, so there's no Nadi dosha and your temperaments sit well together. Most of the gap comes from Bhakoot and Graha Maitri, which traditionally point to different ways of handling money and family decisions. That's something to talk through openly, not a reason on its own to say no.</div>
            </div>
          </div>
        </section>


        <section className="rule" aria-labelledby="report">
          <div className="wrap">
            <p className="eyebrow">What you actually get</p>
            <h2 id="report" style={{marginTop: '14px'}}>Not just <em>what.</em> When, and why.</h2>
            <div className="report">
              <div className="report-head"><b>Love &amp; Marriage Report</b><span>Sample preview · For guidance purposes</span></div>
              <ol>
                <li><b>Marriage timing</b><span>Favourable windows from your dasha periods, not fixed dates</span></li>
                <li><b>Your 7th house &amp; Venus</b><span>What your chart indicates about the partner and relationship you're drawn to</span></li>
                <li className="blur" aria-hidden="true"><b>Patterns</b><span>Why the same things keep repeating, and what your chart suggests</span></li>
                <li className="blur" aria-hidden="true"><b>Compatibility</b><span>Full gun milan plus the meaning, when you add a partner's details</span></li>
                <li className="blur" aria-hidden="true"><b>Follow-ups</b><span>Ask about anything in the report, 24/7</span></li>
              </ol>
              <div className="report-lock"><p>Also inside: patterns, compatibility and unlimited follow-ups on your report.</p><a className="btn ghost" href="#plans">See what's included →</a></div>
            </div>
          </div>
        </section>


        <section className="rule" aria-labelledby="how">
          <div className="wrap">
            <p className="eyebrow">How it works</p>
            <h2 id="how" style={{marginTop: '14px'}}>Under a minute to your first answer.</h2>
            <ol className="steps">
              <li><b>Enter birth details</b><span>Date, time and place. Takes under a minute.</span></li>
              <li><b>Kundli is computed</b><span>From Swiss Ephemeris astronomy data.</span></li>
              <li><b>100+ placements, read together</b><span>Houses, dashas, transits and yogas.</span></li>
              <li><b>Answer in plain language</b><span>Then ask follow-ups like a normal chat.</span></li>
            </ol>
          </div>
        </section>








        <section aria-labelledby="priv">
          <div className="wrap">
            <div className="privacy">
              <svg viewBox="0 0 72 72" aria-hidden="true"><rect x="14" y="30" width="44" height="34" rx="8" fill="none" stroke="#ffccfd" strokeWidth="2.5"/><path d="M24 30v-8a12 12 0 0 1 24 0v8" fill="none" stroke="#ffccfd" strokeWidth="2.5"/><circle cx="36" cy="46" r="4" fill="#f2cf8a"/></svg>
              <div>
                <h2 id="priv">Ask what you'd <em>never say out loud.</em></h2>
                <p>Your birth details and questions are encrypted and never sold. No one at the next family function will know what you asked.</p>
              </div>
            </div>
          </div>
        </section>


        <section className="rule" aria-labelledby="faq">
          <div className="wrap">
            <p className="eyebrow">Questions</p>
            <h2 id="faq" style={{marginTop: '14px'}}>Before you ask.</h2>
            <div className="faq">
              <details><summary>Kya ye sach mein accurate hai?</summary><p>Your kundli is computed from Swiss Ephemeris astronomical data, and Aura shows you which placement and period each insight comes from. It points to favourable windows, not fixed dates. That's how Vedic timing actually works.</p></details>
              <details><summary>Will it tell me if my partner is cheating, or if my ex will come back?</summary><p>No. Aura reads <em>your</em> chart, not someone else's private life. It can show your relationship patterns and the periods that favour big decisions.</p></details>
              <details><summary>Birth time nahi pata?</summary><p>It works. An approximate time still builds the chart and most of it can be read. An exact time sharpens the lagna and the dasha timing, so give the closest time you can.</p></details>
              <details><summary>What happens after my free question?</summary><p>You get a complete answer. If you want timing windows, patterns and the full picture, the Love &amp; Marriage Report goes deeper.</p></details>
              <details><summary>App download karna padega?</summary><p>No. It works right here in your browser. There's also an <a href="https://play.google.com/store/apps/details?id=com.astroaura.auraai" rel="noopener" target="_blank">Android app</a>.</p></details>
              <details><summary>Is my data private?</summary><p>Yes. It's encrypted and never sold.</p></details>
              <details><summary>What is love astrology?</summary><p>Love astrology reads the part of your birth chart that describes relationships: the 7th house and its lord for partnership, Venus for attraction and affection, the 5th for romance, and the dasha period you are running for timing. Read together they describe the kind of partnership your chart leans toward and which phase you are in, rather than naming a person.</p></details>
              <details><summary>Can astrology predict marriage timing from a date of birth?</summary><p>Marriage timing needs the date, the time and the place, not the date alone. Those three fix the lagna, the house cusps and the Vimshottari dasha sequence, and it is the dasha and the transits of Jupiter and Saturn that mark the favourable windows. A chart shows windows, not fixed dates, and anyone offering you an exact date is guessing.</p></details>
              <details><summary>What does a gun milan score actually mean?</summary><p>Ashtakoot gun milan scores eight factors for a total of 36 gunas. Traditionally 18 or more is considered workable, but the number on its own says little: Nadi and Bhakoot carry the most weight, and a high score with a Nadi dosha is a different situation from a middling score without one. The score is a starting point for a conversation, not a verdict on a relationship.</p></details>
              <details><summary>Is the love astrology chat free?</summary><p>Your first question is free, with no card. Aura AI builds your kundli from your date, time and place of birth, then chats with you about your relationship in Hindi or English. After the free question, further chats are paid from wallet credits, with no subscription.</p></details>
            </div>
          </div>
        </section>







        <div className="sticky" id="sticky"><span>First question free · 60 seconds</span><button className="btn" type="button" data-ask>Ask your love question free →</button></div>
        {/* PRICING. Three real destinations, no placeholders: free question ->
              the chat flow, report -> the Love Life Report on /reports,
              membership -> SubscribeSheet. The report price is read live from
              PricingContext so it tracks currency and any member rate. */}
        <section className="rule" id="plans" aria-labelledby="offer">
          <div className="wrap">
            <p className="eyebrow">Pricing</p>
            <h2 id="offer">Start free. <em>Go deeper when you're ready.</em></h2>

            <div className="plans">
              <div className="plan">
                <h3>Free question</h3>
                <p className="price">{formatPrice(0)}</p>
                <ul>
                  <li>1 love question answered from your chart</li>
                  <li>Your kundli, computed</li>
                  <li>No card needed</li>
                </ul>
                <button className="btn ghost" type="button" data-ask>
                  Ask Free
                </button>
              </div>

              <div className="plan star">
                <h3>Love Life Report</h3>
                <p className="price">
                  {reportPrice ? formatPrice(reportPrice) : ""}
                </p>
                <ul>
                  <li>Marriage timing windows</li>
                  <li>7th house and Venus analysis</li>
                  <li>Relationship patterns</li>
                  <li>Dasha phases ahead</li>
                  <li>Follow-up questions on your report</li>
                </ul>
                <button className="btn" type="button" data-report>
                  Get My Report
                </button>
              </div>

              <div className="plan">
                <h3>Aura Plus</h3>
                <p className="price">
                  Monthly <small>membership</small>
                </p>
                <ul>
                  <li>Unlimited follow-up questions</li>
                  <li>Ask any time, 24/7</li>
                  <li>Member rates on every report</li>
                </ul>
                <button className="btn ghost" type="button" data-subscribe>
                  Get Membership
                </button>
              </div>
            </div>

            <p className="pay">UPI, cards and wallets. Instant access.</p>
          </div>
        </section>

        {/* Closing card: the app's landing-page card format (FooterEmailBanner)
            carrying this page's own copy. Rebuilt rather than reused because
            that component is shared by four routes and takes no copy props. */}
        <section className="lov-close" aria-labelledby="close">
          <div className="lov-close__card">
            <h2 id="close" className="lov-close__title">
              One question. Free. <em>Read from your chart, not someone's guess.</em>
            </h2>
            <p className="lov-close__sub">
              Pehla sawaal bilkul free. Apni kundli se, 60 second mein.
            </p>
            <button className="lov-close__cta" type="button" data-ask>
              Ask My Love Question, Free
            </button>
            <p className="lov-close__note">
              First question free <span aria-hidden="true">&middot;</span> No card needed{" "}
              <span aria-hidden="true">&middot;</span> Private &amp; encrypted
            </p>
          </div>
        </section>
      </main>

      {/* The site footer is suppressed on this route, so Privacy and Terms
          still need a home: Google Ads expects a landing page to surface them. */}
      <footer className="lov-foot">
        <div className="wrap">
          <span>&copy; Aura AI</span>
          <span>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms-and-conditions">Terms</Link>
          </span>
        </div>
      </footer>

      <SubscribeSheet
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        source="love_page"
      />

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </div>
  );
}
